import React, { createContext, Component } from 'react';

// Create SocketContext
export const SocketContext = createContext();

// Define ApiMessageHandler class inside the SocketContext
class ApiMessageHandler {
    constructor(socket) {
        this.socket = socket;
        this.requestCallbacks = new Map(); // Registry for callbacks by request ID
        this.onConnectedClients = null;
        this.webRTCcallback = null;
        this.updatesCallbacks = new Map();  // Registry for callbacks by device ID
    }

    static generateRequestId() {
        return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    setWebRTCCallback(callback) {
        this.webRTCcallback = callback;
    }

    addUpdatesCallback(deviceId, updatesCallback) {
        this.updatesCallbacks.set(deviceId, updatesCallback);
    }

    removeUpdatesCallback(deviceId) {
        this.updatesCallbacks.delete(deviceId);
    }

    sendRequest(message, callback) {
        if (this.socket.readyState === WebSocket.CLOSED) {
            callback.on_failure("Socket is closed");
            return;
        }

        const requestId = ApiMessageHandler.generateRequestId();
        message.request_id = requestId;

        this.requestCallbacks.set(requestId, callback);

        if (callback.on_loading) callback.on_loading();

        try {
            this.socket.send(JSON.stringify(message));
        } catch (error) {
            this.requestCallbacks.delete(requestId);
            if (callback.on_failure) callback.on_failure("Failed to send request");
        }
    }

    handleMessage(event) {
        console.log("ApiHandler handleMessage", event);
        let socketMsg;
        try {
            socketMsg = JSON.parse(event.data);
        } catch (error) {
            console.error("Failed to parse incoming message:", event.data);
            return;
        }

        // Dedicated handling for "connected_clients" messages
        if (socketMsg.method === "connected_clients") {
            if (this.onConnectedClients) {
                console.log("onConnectedClients ready to handle clients update")
                this.onConnectedClients(socketMsg.clients);
                return;
            } else {
                console.log("onConnectedClients is not initialized to handle clients");
            }
        }

        // Dedicated handling for WebRTC messages
        else if (socketMsg.method === 'sdp' && socketMsg.type === 'offer') {
            if (this.webRTCcallback) {
                console.log('Remote Offer received and delivered to WebRTC session');
                this.webRTCcallback.on_remote_offer(socketMsg)
            } else {
                console.log('Remote Offer received but there is no callback to deliver it');
            }
        }

        else if (socketMsg.type === 'candidate') {
            if (this.webRTCcallback) {
                console.log('Remote ICE candidate received and delivered to WebRTC session');
            } else {
                console.log('Remote ICE candidate  received but there is no callback to deliver it');
                this.webRTCcallback.on_remote_ice_candidates(socketMsg.ices, socketMsg.type);
            }
        }


        else if (socketMsg.method === "api") {
            if (socketMsg.request_id && this.requestCallbacks.has(socketMsg.request_id)) {
                const callback = this.requestCallbacks.get(socketMsg.request_id);
                this.requestCallbacks.delete(socketMsg.request_id);
                if (socketMsg.error) {
                    if (callback.on_failure) callback.on_failure(socketMsg.error);
                } else {
                    if (callback.on_success) callback.on_success(socketMsg.payload);
                }
                return;
            }
        } else if (socketMsg.method === 'update') {
            console.log('received api update');
            this.handleUpdate(socketMsg);
        } else {
            console.log("Unsupported message: ", event.data);
        }
    }

    handleUpdate(updateMsg) {
        console.error("handleUpdate:", updateMsg);

        if (updateMsg.type === "recording_added") {
            let recording = updateMsg.payload.recording
            let deviceId = recording.device_id
            let deviceUpdateHandler = this.updatesCallbacks.get(deviceId)
            if (deviceUpdateHandler) {
                deviceUpdateHandler.on_update(updateMsg)
            } else {
                console.log("No subscribers for update:", updateMsg);
            }
        } else {
            // TODO think of having generic updates and device-specific updates
            console.log("No subscriber for generic updates:", updateMsg);
        }
    }

    close() {
        for (const [requestId, callback] of this.requestCallbacks.entries()) {
            if (callback.on_failure) callback.on_failure("Socket closed");
        }
        this.updatesCallbacks.clear()
        this.requestCallbacks.clear();
    }
}

export class SocketProvider extends Component {
    constructor(props) {
        super(props);
        this.state = {
            socket: null,
            isConnected: false,
            apiHandler: null,
            isPeerConnected: false
        };
    }

    establishSocketConnection(roomId, roomPass) {
        const URL_WEB_SOCKET = `wss://signaling.api.kyivapp.team/ws/groundstation?id=${roomId}&password=${roomPass}`;

        const ws = new WebSocket(URL_WEB_SOCKET);

        this.apiHandler = new ApiMessageHandler(ws);

        // Assign the connected_clients callback in the ApiHandler
        this.apiHandler.onConnectedClients = (clients) => {
            const connected = Array.isArray(clients) && clients.length > 0;
            console.log('SocketProvider onConnectedClients isPeerConnected ', connected);
            this.setState({ isPeerConnected: connected });
        };

        // Optionally update state for context consumers.
        this.setState({ apiHandler: this.apiHandler });

        ws.onopen = () => {
            console.log('WebSocket connection opened');
            this.setState({ isConnected: true, socket: ws });
            // Create the ApiMessageHandler instance and assign to instance property.
            console.log('api handler is initialized');
        };

        ws.onclose = () => {
            console.log('WebSocket connection closed');
            this.setState({ isConnected: false });
        };

        ws.onerror = (e) => {
            console.log('WebSocket connection error', e);
            //this.setState({ isConnected: false });
        }

        ws.onmessage = (event) => {
            console.log(`WebSocket message: ${event.data}`);
            const { apiHandler } = this.state;
            if (apiHandler) {
                apiHandler.handleMessage(event);
            } else {
                console.log("ApiHandler is not initialized yet.");
            }
        };

        return ws;
    }

    componentDidMount() {
        console.log("SocketProvider componentDidMount start");
        const { roomId, password } = this.props; // Get roomId and password from props
        if (roomId && password) {
            this.establishSocketConnection(roomId, password); // Use the provided roomId and password
        } else {
            console.log("Room ID and Password not provided");
        }

        console.log("SocketProvider componentDidMount");
    }

    componentWillUnmount() {
        const { socket } = this.state;
        if (socket) {
            socket.close();
        }
    }

    render() {
        const { socket, isConnected, apiHandler, isPeerConnected } = this.state;

        return (
            <SocketContext.Provider value={{ socket, isConnected, apiHandler, isPeerConnected }}>
                {this.props.children}
            </SocketContext.Provider>
        );
    }
}
